import React, { useEffect, useMemo, useState } from "react";
import { useResendVerificationEmail } from "../../hooks/auth";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Fade,
  Typography,
  Alert,
  useTheme,
  Stack,
  Stepper,
  Step,
  StepLabel,
  Link as MuiLink} from "@mui/material";
import { Send as SendIcon, MarkEmailRead, HourglassEmpty } from "@mui/icons-material";
import { ApiError } from "../../error/errorTypes";
import { LOGIN_ROUTE_PATH } from "../../utils/constants/AppConstants";

type LocationState = { email?: string } | null;

const VerifyEmailPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state as LocationState) || null;
  const email = useMemo(() => state?.email || "", [state]);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cooldownEndsAt, setCooldownEndsAt] = useState<number | null>(null);
  const [secondsLeft, setSecondsLeft] = useState<number>(0);

  const { mutate: resendVerificationEmail, isPending: isSending } = useResendVerificationEmail();

  const handleResend = () => {
    setMessage(null);
    setError(null);
    
    const publicBase = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
    const path = `${publicBase}${"/email/verify"}`;
    const verificationCallbackUrl = new URL(path, window.location.origin).toString();
    
    resendVerificationEmail(
      { email, verificationCallbackUrl },
      {
        onSuccess: () => {
          setMessage("Verification email sent. Please check your inbox and spam folder.");
          const endsAt = Date.now() + 60_000; // 60s cooldown
          setCooldownEndsAt(endsAt);
          setSecondsLeft(60);
        },
        onError: (err) => {
          if (err instanceof ApiError) {
            setError(err.userMessage || err.message);
          } else {
            setError("Could not send verification email. Please try again.");
          }
        }
      }
    );
  };

  useEffect(() => {
    if (!cooldownEndsAt) return;
    const interval = setInterval(() => {
      const left = Math.max(0, Math.ceil((cooldownEndsAt - Date.now()) / 1000));
      setSecondsLeft(left);
      if (left <= 0) {
        setCooldownEndsAt(null);
        clearInterval(interval);
      }
    }, 250);
    return () => clearInterval(interval);
  }, [cooldownEndsAt]);

  const isCooldown = cooldownEndsAt !== null;

  const webmailLink = useMemo(() => {
    const domain = (email.split("@")[1] || "").toLowerCase();
    if (domain.includes("gmail.")) return "https://mail.google.com/";
    if (domain.includes("outlook.") || domain.includes("live.") || domain.includes("hotmail.")) return "https://outlook.live.com/mail/";
    if (domain.includes("yahoo.")) return "https://mail.yahoo.com/";
    if (domain.includes("proton.")) return "https://mail.proton.me/";
    return "";
  }, [email]);


  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.secondary.main}15 100%)`,
        display: "flex",
        alignItems: "center",
        py: 4}}
    >
      <Container maxWidth="sm">
        <Fade in={true} timeout={700}>
          <Card
            sx={{
              background: theme.palette.background.paper,
              backdropFilter: "blur(10px)",
              border: `1px solid ${theme.palette.divider}`}}
          >
            <CardContent sx={{ p: 5 }}>
              <Box sx={{ textAlign: "center", mb: 3 }}>
                <Box
                  sx={{
                    display: "inline-flex",
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    borderRadius: 3,
                    p: 2,
                    mb: 3}}
                >
                  <MarkEmailRead sx={{ fontSize: "2.5rem", color: "white" }} />
                </Box>
                <Typography
                  variant="h4"
                  gutterBottom
                  sx={{
                    fontWeight: 700,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent"}}
                >
                  Verify your email
                </Typography>
                {email ? (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body1" color="text.secondary">
                      We sent a verification link to:
                    </Typography>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        fontWeight: 600, 
                        color: theme.palette.primary.main,
                        wordBreak: 'break-all',
                        mt: 1
                      }}
                    >
                      {email}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Click the link in the email to activate your account.
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="body1" color="text.secondary">
                    Please check your email for a verification link to activate your account.
                  </Typography>
                )}
                {!email && (
                  <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
                    We couldn't determine your email from the previous step. Please go back to sign in and try again.
                  </Alert>
                )}
              </Box>

              <Stepper activeStep={1} alternativeLabel sx={{ mb: 3 }}>
                <Step>
                  <StepLabel>Account created</StepLabel>
                </Step>
                <Step>
                  <StepLabel>Check your inbox</StepLabel>
                </Step>
                <Step>
                  <StepLabel>Verify</StepLabel>
                </Step>
              </Stepper>

              {message && (
                <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} role="status" aria-live="polite">
                  {message}
                </Alert>
              )}
              {error && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} role="alert">
                  {error}
                </Alert>
              )}

              <Button
                fullWidth
                variant="contained"
                size="large"
                disabled={!email || isSending || isCooldown}
                onClick={handleResend}
                startIcon={isCooldown ? <HourglassEmpty /> : <SendIcon />}
                sx={{
                  py: 1.5,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  "&:hover": {
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`}}}
              >
                {isSending ? "Sending..." : isCooldown ? `Resend available in ${secondsLeft}s` : "Resend verification email"}
              </Button>

              <Stack direction="column" spacing={1.25} sx={{ mt: 2, color: "text.secondary" }}>
                <Typography variant="body2">
                  Didn’t receive the email? Check your spam folder, or click Resend.
                </Typography>
                {webmailLink && (
                  <Typography variant="body2">
                    Quick open: <MuiLink href={webmailLink} target="_blank" rel="noopener">open your inbox</MuiLink>
                  </Typography>
                )}
              </Stack>

              <Box sx={{ textAlign: "center", mt: 3 }}>
                <Button variant="text" onClick={() => navigate(LOGIN_ROUTE_PATH)}>
                  Back to sign in
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Fade>
      </Container>
    </Box>
  );
};

export default VerifyEmailPage;


